import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { z } from 'zod';
import { useTranslation } from 'utils/modifiedTranslation';
import Form, { FORM_INPUT_TYPES as INPUT_TYPES } from 'new-ui/Components/Form';
import {
  PopupTitle, PopupContent,
} from 'new-ui/Components/Popup';
import { mapValidationToInputHelpers } from 'functions/mapValidationToInputHelpers';
import { splitDoctorName } from './helpers';

const DOCTOR_FIRST_NAME_MAX_LENGTH = 45;
const DOCTOR_LAST_NAME_MAX_LENGTH = 45;

const getFormSchema = ({ t }) => z.object({
  doctorEmail: z.email(t('general.email_invalid_format')),
  doctorName: z
    .string()
    .max(DOCTOR_FIRST_NAME_MAX_LENGTH + DOCTOR_LAST_NAME_MAX_LENGTH, t('general.input_too_long'))
    .optional(),
});

export const ShareWithDoctorPopup = ({ title, onSubmit, isSent }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    doctorName: '',
    doctorEmail: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const inputs = {
    doctorName: {
      title: t('share_with_doctor.doctor_name'),
      type: INPUT_TYPES.INPUT,
      required: false,
    },
    doctorEmail: {
      title: t('share_with_doctor.doctor_email'),
      type: INPUT_TYPES.INPUT,
      email: true,
      required: true,
    },
  };

  const data = { ...formData };

  const handleSubmit = () => {
    setFormData({ ...data });
    const validation = getFormSchema({ t }).safeParse(data);
    if (!validation.success) {
      setFormErrors(mapValidationToInputHelpers(validation));
      return;
    }
    const { doctorName, ...rest } = data;
    let doctorFirstName = '';
    let doctorLastName = '';
    if (doctorName?.trim()) {
      ({ firstName: doctorFirstName, lastName: doctorLastName } = splitDoctorName(doctorName));
      if (DOCTOR_FIRST_NAME_MAX_LENGTH < doctorFirstName.length || DOCTOR_LAST_NAME_MAX_LENGTH < doctorLastName.length) {
        setFormErrors({
          doctorName: {
            type: 'error',
            value: 'Too long',
          },
        });
        return;
      }
    }
    setFormErrors({});

    onSubmit({ ...rest, doctorFirstName, doctorLastName });
  };

  return (
    <div>
      <PopupTitle title={title} />
      <PopupContent>
        {isSent ? (
          <div>
            {t('share_with_doctor.successfully_sent')}
          </div>
        ) : (
          <Form
            inputs={inputs}
            data={data}
            popupMode
            submit={{ title: t('share_with_doctor.send'), action: handleSubmit }}
            inputHelperText={formErrors}
          />
        )}
      </PopupContent>
    </div>
  );
};

ShareWithDoctorPopup.propTypes = {
  title: PropTypes.string.isRequired,
  isSent: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
